// Libs
import * as admin from "firebase/firestore";
import { z } from "zod";

// Local
import { dropUndefined } from "../utils/dropUndefined";
import { guardIsPlainObject } from "../utils/isPlainObject";
import { NotFoundError } from "../error-classes";
import {
  AccountingSyncStiltDoc,
  AccountingSyncStiltDocSchema,
} from "./accounting-sync";
import { zFallback } from "../utils/zod-fallback";

type DocumentData = admin.DocumentData;
type DocumentSnapshot = admin.DocumentSnapshot;
type TimestampType = admin.Timestamp;
const Timestamp = admin.Timestamp;

// #region SECTION: Types & Interfaces
export interface Vendor {
  name: string;
  email: string[] | null;
  phone: string[] | null;
  notes: string | null;
  website: string | null;
  logoURL: string | null;
  addressLine1: string;
  addressLine2: string | null;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  timestampCreated: TimestampType;
  timestampLastModified: TimestampType;
  createdBy: string;
  lastModifiedBy: string;
  tags: string[];
  customData: { [key: string]: any };
  accountingSync?: AccountingSyncStiltDoc;
  deleted: boolean;
  paymentTerms: string | null;
}

export interface ExistingVendor extends Vendor {
  id: string;
  refPath: string;
}

/***********************/
// FOR THE API
/***********************/
type withoutTimestamps = Omit<
  Vendor,
  "timestampCreated" | "timestampLastModified"
>;

export type Vendor_CreateAPI = withoutTimestamps & {
  siteKey: string;
};

export type Vendor_UpdateAPI = Omit<
  Partial<ExistingVendor>,
  "timestampCreated" | "timestampLastModified"
>;

// Maintain backward compatibility
export type APICreateVendor = Vendor_CreateAPI;
export type APIUpdateVendor = Vendor_UpdateAPI;

// #endregion Types & Interfaces

// #region SECTION: Functions
/** Utilities for interacting with Vendor objects */
export const VendorManager = {
  /** Drop `id` and `refPath` before saving to the database. Drop undefined */
  convertForFirestore: convertVendorForFirestore,
  /** Validate a Vendor doc. Use for writing to the database or reading from the user. */
  parse: validateVendor,
  /** Validate a Vendor doc, with fallbacks. Use for reading from the database. */
  parseWithFallbacks: validateVendorWithFallbacks,
  /** Validate a new vendor. For the create endpoint. */
  parseCreate: validateVendor_Create,
  /** Validate an existing vendor. For the update endpoint. */
  parseUpdate: validateVendor_Update,
  /** Throws NotFoundError if document does not exist. */
  createFromFirestoreSnapshot: createFromFirestoreSnapshot,
  /** Convert vendor to a readable string format for LLM */
  convertForLLM,
} as const;

/** Drop `id` and `refPath` before saving to the database. Drop undefined values */
function convertVendorForFirestore(
  vendor: ExistingVendor | APIUpdateVendor,
): DocumentData {
  const local = Object.assign({}, vendor);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { id, refPath, ...rest } = local;
  return dropUndefined(rest);
}

function validateVendor(value: unknown): Vendor {
  if (!guardIsPlainObject(value)) {
    throw new Error(`value not an object: ${value}`);
  }
  const result = vendorSchema.safeParse(value);
  if (!result.success) {
    throw new Error(`Invalid vendor: ${result.error}`);
  }
  return result.data;
}

function validateVendorWithFallbacks(value: unknown): Vendor {
  if (!guardIsPlainObject(value)) {
    throw new Error(`value not an object: ${value}`);
  }
  const result = vendorSchemaWithFallbacks.parse(value);
  return result;
}

function validateVendor_Create(value: unknown): APICreateVendor {
  if (!guardIsPlainObject(value)) {
    throw new Error(`value not an object: ${value}`);
  }
  return vendorSchema_CreateAPI.parse(value);
}

function validateVendor_Update(value: unknown): APIUpdateVendor {
  if (!guardIsPlainObject(value)) {
    throw new Error(`value not an object: ${value}`);
  }
  return vendorSchema_UpdateAPI.parse(value);
}

function createFromFirestoreSnapshot(
  snapshot: DocumentSnapshot,
): ExistingVendor {
  if (!snapshot.exists) {
    throw new NotFoundError(`Document does not exist. ${snapshot.ref.path}`);
  }
  return {
    id: snapshot.id,
    refPath: snapshot.ref.path,
    ...validateVendorWithFallbacks(snapshot.data()),
  };
}

function convertForLLM(vendor: ExistingVendor): string {
  const { id, name, website, tags } = vendor;

  const parts: string[] = [];

  // Add basic info
  parts.push(`This is a vendor with an internal ID of ${id}`);
  parts.push(`named "${name}"`);

  // Add website if present
  if (website) {
    parts.push(`with website: ${website}`);
  }

  // Add tags if present
  if (tags && tags.length > 0) {
    parts.push(`with tags: ${tags.join(", ")}`);
  }

  return parts.join(" ");
}
// #endregion

// #region SECTION: Schemas
// Used when writing to the DB or reading from the user.
const vendorSchema = z.object({
  name: z.string().min(1).max(200),
  email: z.array(z.string().min(0).max(200)).nullable(),
  phone: z.array(z.string().min(0).max(200)).nullable(),
  notes: z.string().min(0).max(10000).nullable(),
  website: z.string().min(0).max(200).nullable(),
  logoURL: z.string().min(0).max(2000).nullable(),
  addressLine1: z.string().min(0).max(200),
  addressLine2: z.string().min(0).max(200).nullable(),
  city: z.string().min(0).max(200),
  state: z.string().min(0).max(200),
  zipCode: z.string().min(0).max(200),
  country: z.string().min(1).max(200),
  timestampCreated: z.instanceof(Timestamp),
  timestampLastModified: z.instanceof(Timestamp),
  createdBy: z.string().min(1).max(200),
  lastModifiedBy: z.string().min(1).max(200),
  tags: z.array(z.string().min(1).max(200)),
  customData: z.record(z.any()),
  accountingSync: AccountingSyncStiltDocSchema.optional(),
  deleted: z.boolean(),
  paymentTerms: z.string().min(1).max(200).nullable(),
});

// Used when validating data coming from the database.
const vendorSchemaWithFallbacks = z.object({
  name: zFallback(
    z.string().min(1).max(200),
    "unknown",
    "vendorSchemaWithFallbacks: 'name'",
  ),
  email: zFallback(
    z.array(z.string().min(0).max(200)).nullable(),
    null,
    "vendorSchemaWithFallbacks: 'email'",
  ),
  phone: zFallback(
    z.array(z.string().min(0).max(200)).nullable(),
    null,
    "vendorSchemaWithFallbacks: 'phone'",
  ),
  notes: zFallback(
    z.string().min(0).max(10000).nullable(),
    null,
    "vendorSchemaWithFallbacks: 'notes'",
  ),
  website: zFallback(
    z.string().min(0).max(200).nullable(),
    null,
    "vendorSchemaWithFallbacks: 'website'",
  ),
  logoURL: zFallback(
    z.string().min(0).max(2000).nullable(),
    null,
    "vendorSchemaWithFallbacks: 'logoURL'",
  ),
  addressLine1: zFallback(
    z.string().min(0).max(200),
    "unknown",
    "vendorSchemaWithFallbacks: 'addressLine1'",
  ),
  addressLine2: zFallback(
    z.string().min(0).max(200).nullable(),
    null,
    "vendorSchemaWithFallbacks: 'addressLine2'",
  ),
  city: zFallback(
    z.string().min(0).max(200),
    "unknown",
    "vendorSchemaWithFallbacks: 'city'",
  ),
  state: zFallback(
    z.string().min(0).max(200),
    "unknown",
    "vendorSchemaWithFallbacks: 'state'",
  ),
  zipCode: zFallback(
    z.string().min(0).max(200),
    "unknown",
    "vendorSchemaWithFallbacks: 'zipCode'",
  ),
  country: zFallback(
    z.string().min(1).max(200),
    "USA",
    "vendorSchemaWithFallbacks: 'country'",
  ),
  timestampCreated: zFallback(
    z.instanceof(Timestamp),
    Timestamp.now(),
    "vendorSchemaWithFallbacks: 'timestampCreated'",
  ),
  timestampLastModified: zFallback(
    z.instanceof(Timestamp),
    Timestamp.now(),
    "vendorSchemaWithFallbacks: 'timestampLastModified'",
  ),
  createdBy: zFallback(
    z.string().min(1).max(200),
    "unknown",
    "vendorSchemaWithFallbacks: 'createdBy'",
  ),
  lastModifiedBy: zFallback(
    z.string().min(1).max(200),
    "unknown",
    "vendorSchemaWithFallbacks: 'lastModifiedBy'",
  ),
  tags: zFallback(
    z.array(z.string().min(1).max(200)),
    [],
    "vendorSchemaWithFallbacks: 'tags'",
  ),
  customData: zFallback(
    z.record(z.any()),
    {},
    "vendorSchemaWithFallbacks: 'customData'",
  ),
  accountingSync: zFallback(
    AccountingSyncStiltDocSchema.optional(),
    undefined,
    "vendorSchemaWithFallbacks: 'accountingSync'",
  ),
  deleted: zFallback(
    z.boolean(),
    false,
    "vendorSchemaWithFallbacks: 'deleted'",
  ),
  paymentTerms: zFallback(
    z.string().min(1).max(200).nullable(),
    null,
    "vendorSchemaWithFallbacks: 'paymentTerms'",
  ),
});

/***********************/
// FOR THE API
/***********************/
const withoutTimestampsSchema = vendorSchema.omit({
  timestampCreated: true,
  timestampLastModified: true,
});

// Used for interacting with the Create endpoint.
const vendorSchema_CreateAPI = withoutTimestampsSchema.extend({
  siteKey: z.string().min(1).max(100),
});

// Used for interacting with the Update endpoint
const vendorSchema_UpdateAPI = withoutTimestampsSchema
  .extend({
    id: z.string().min(1).max(200),
    refPath: z.string().min(1).max(400),
  })
  .partial();
// #endregion
